import React, { useRef } from 'react';
import ModalPortal from '../shared/ModalPortal';
import { CancelIcon } from '../../svgs/CancelIcon';
import { TempleHSLogo } from '../../svgs/TempleHSLogo';
import Button from '../shared/Button';

interface TranscriptionCompletedData {
  message: string;
  jobId: string;
  status: 'COMPLETED';
  duration: number;
  transcriptionCost: number;
  transcript: string;
  timestamp: string;
  title?: string;
}

interface TranscriptionCostModalProps {
  isVisible: boolean;
  onClose: () => void;
  transcriptionData: TranscriptionCompletedData | null;
}

export const TranscriptionCostModal: React.FC<TranscriptionCostModalProps> = ({
  isVisible,
  onClose,
  transcriptionData,
}) => {
  const modalContentRef = useRef<HTMLDivElement>(null);

  const formatCurrency = (amount: number | undefined) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours} hour${hours !== 1 ? 's' : ''}${remainingMinutes > 0 ? ` ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}` : ''}`;
  };

  if (!isVisible || !transcriptionData) return null;

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[999] cursor-pointer flex items-center justify-center bg-black bg-opacity-20"
        onClick={onClose}
      >
        <div
          ref={modalContentRef}
          className="bg-gray-50 cursor-default rounded-lg px-8 py-14 w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className='flex justify-between items-center mb-4'>
            <div className='flex flex-col'>
              <small className='text-[10px] font-semibold text-gray-400'>
                {new Date(transcriptionData.timestamp).toLocaleDateString()}
              </small>
              <small className='text-xs text-gray-500'>
                {transcriptionData.title || `Transcription ${transcriptionData.jobId}`}
              </small>
            </div>
            <div className="relative">
              <button
                onClick={onClose}
                className="close-button text-gray-400 hover:text-gray-500"
              >
                <CancelIcon className="h-6 w-6" />
              </button>
              <div className="invoice-logo hidden absolute right-0">
                <TempleHSLogo width={96} height={38} className="h-8 w-auto" />
              </div>
            </div>
          </div>
          
          <div className="mb-4">
            <h3 className="text-3xl font-bold text-gray-700">
              Transcription Complete
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              Your meeting has been successfully transcribed
            </p>
          </div>

          <div className="space-y-5 mt-8">
            <div className="flex justify-between">
              <span className='font-medium text-gray-500'>Recording Duration:</span>
              <span className='font-bold text-gray-500'>
                {formatDuration(transcriptionData.duration)}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className='font-medium text-gray-500'>Transcription Cost:</span>
              <span className='font-bold text-gray-500'>
                {formatCurrency(transcriptionData.transcriptionCost)}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className='font-medium text-gray-500'>Rate:</span>
              <span className='font-bold text-gray-500'>$0.10 per minute</span>
            </div>
            
            <hr />
            
            <div className="flex justify-end font-semibold text-lg">
              <span className='font-bold text-3xl text-gray-500'>
                {formatCurrency(transcriptionData.transcriptionCost)}
              </span>
            </div>
          </div>

          <div className='mt-6 p-4 bg-blue-50 rounded-md border border-blue-200'>
            <p className='text-sm text-blue-800 font-medium mb-2'>
              💳 Payment Information
            </p>
            <p className='text-xs text-blue-700'>
              This is the total cost of your transcription service. You will be automatically billed on your saved card. The charge will appear on your next billing statement.
            </p>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              onClick={onClose}
              variant="primary"
              label="Got it"
              additionalClassname="bg-[#3D874E] text-white rounded-md px-6 py-2"
            />
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
